/**
 * RateLab V9 — independent deterministic financial engine.
 * Reference: TRITAL_Care_RateLab_v9_Universal(1).xlsx (25 September 2026)
 * Sheets: Calculator, Settings, Rate Cards, Engine.
 *
 * The rate, employer-cost, and tax-floor defaults below reproduce the attached
 * workbook, NOT independently verified legal minimums or current GSA data.
 * GSA inputs must come from a verified fiscal-year/location lookup.
 * Never treat a GSA allowance as proof that a stipend is tax-free.
 */
const V9_SETTINGS = Object.freeze({
  greenMargin: 0.15,
  minimumMargin: 0.12,
  burden: 0.22,
  clinicalFixedCost: 416,
  nonClinicalFixedCost: 75,
  overtimePayMultiplier: 1.5,
  defaultOtBillMultiplier: 1.5,
  illustrativeWithholdingPct: 0.2265,
});
const V9_DISCIPLINE_FLOORS = Object.freeze({
  "RN": 33.14,
  "LPN / LVN": 23.91,
  "CNA / CMA": 16.32,
  "Allied / Tech (Rad, CT, Surg, Lab)": 26.91,
  "Therapist (PT / OT / SLP / RT)": 37.09,
  "Non-Clinical": 0,
});
const V9_STATE_WAGES = Object.freeze({
 AL:7.25,AK:14,AZ:15.15,AR:11,CA:16.90,CO:15.16,CT:16.94,DE:15,DC:18.40,
 FL:15,GA:7.25,HI:16,ID:7.25,IL:15,IN:7.25,IA:7.25,KS:7.25,KY:7.25,
 LA:7.25,ME:15.10,MD:15,MA:15,MI:15,MN:11.41,MS:7.25,MO:15,MT:10.85,
 NE:15,NV:12,NH:7.25,NJ:15.92,NM:12,NY:16,NC:7.25,ND:7.25,OH:11,
 OK:7.25,OR:16.80,PA:7.25,RI:16,SC:7.25,SD:11.85,TN:7.25,TX:7.25,
 UT:7.25,VT:14.42,VA:12.77,WA:17.13,WV:8.75,WI:7.25,WY:7.25
});
// Client-specific financial terms are intentionally not bundled into public JavaScript.
// Presets are stored server-side and require authorized recruiter/admin access.
function amount(v) {
  if (v === null || v === undefined || String(v).trim() === "") return NaN;
  const n = Number(v);
  return Number.isFinite(n) ? n : NaN;
}
function validNonNegative(n) {return Number.isFinite(n) && n>=0;}
function payParts(weeklyOffer, floorWeekly, cap, housingCap) {
 const stipend=Math.min(cap,Math.max(0,weeklyOffer-floorWeekly));
 const housing=Math.min(stipend,housingCap);
 const meals=stipend-housing;
 const taxable=weeklyOffer-stipend;
 return {stipend,housing,meals,taxable};
}
/**
 * An untrusted GSA lookup never becomes a tax eligibility determination.
 * Input dates/ZIP/locality must be validated by the calling UI/API.
 */
function calculateV9(i={}) {
 const problems=[],warnings=[];
 const billRate=amount(i.billRate),weeklyAsk=amount(i.weeklyAsk);
 const shiftCount=amount(i.shiftsPerWeek),shiftHours=amount(i.hoursPerShift);
 const weeks=amount(i.contractWeeks??13);
 if (!validNonNegative(billRate)||billRate<=0) problems.push("Enter a bill rate greater than zero.");
 if (!validNonNegative(weeklyAsk)||weeklyAsk<=0) problems.push("Enter the candidate weekly pay expectation.");
 if (!Number.isInteger(shiftCount)||shiftCount<=0||shiftCount>7) problems.push("Choose a valid shifts-per-week schedule.");
 if (!validNonNegative(shiftHours)||shiftHours<=0||shiftHours>24) problems.push("Choose valid hours per shift.");
 if (!Number.isInteger(weeks)||weeks<1||weeks>52) problems.push("Contract weeks must be 1–52.");
 const discipline=i.discipline||"RN";
 if (!Object.prototype.hasOwnProperty.call(V9_DISCIPLINE_FLOORS,discipline)) problems.push("Select a supported discipline.");
 if (problems.length) return {ready:false,status:"ENTER",problems,warnings};

 const settings={...V9_SETTINGS,...(i.settings||{})};
 const program=i.program||"MANUAL";
 // Authorized presets are applied by the authenticated UI to explicit inputs.
 // Never embed contract terms in a public browser bundle.
 warnings.push("Verify the MSP/VMS fee, orientation and overtime bill basis against the current client contract.");
 const fee=amount(i.manualFeePct??0)/100;
 const orientation=amount(i.manualOrientationHours??0);
 const otBill=amount(i.manualOtBillMultiplier??settings.defaultOtBillMultiplier);
 if(!validNonNegative(fee)||fee>=1||!validNonNegative(orientation)||!validNonNegative(otBill)||otBill<=0)
   return {ready:false,status:"ENTER",problems:["Invalid MSP fee, orientation, or OT multiplier."],warnings};
 const totalHours=shiftCount*shiftHours;
 const otHours=Math.max(0,totalHours-40),straightHours=totalHours-otHours;
 const weightedPayHours=straightHours+settings.overtimePayMultiplier*otHours;
 const billableWeightedHours=straightHours+otBill*otHours;
 const state=(i.state||"").toUpperCase();
 const zip=String(i.zip||"");
 const stateWage=state==="CA"&&discipline!=="Non-Clinical"?25:
  state==="NY"&&/^1[01]\d{3}$/.test(zip)?17:(V9_STATE_WAGES[state]??7.25);
 const taxableFloor=Math.max(V9_DISCIPLINE_FLOORS[discipline],stateWage);
 const floorWeekly=taxableFloor*weightedPayHours;
 if(weeklyAsk<floorWeekly) warnings.push("Candidate ask is below the V9 taxable pay floor; review pay before quoting.");

 const isTravel=i.packageType==="TRAVEL";
 const travelQualified=i.travelQualified===true && i.taxHomeConfirmed===true;
 const gsa=i.gsa||{};
 const lodging=amount(gsa.lodgingDaily),mie=amount(gsa.mieDaily);
 const verifiedGsa=i.gsaVerified===true&&validNonNegative(lodging)&&validNonNegative(mie);
 if(isTravel&&!verifiedGsa) warnings.push("GSA not verified for the entire contract; stipends are not allocated automatically.");
 if(isTravel&&!travelQualified) warnings.push("Travel eligibility or tax home not confirmed; treat proposed reimbursements as taxable until reviewed.");
 const cap=isTravel&&travelQualified&&verifiedGsa?7*(lodging+mie):0;
 const housingCap=isTravel&&travelQualified&&verifiedGsa?7*lodging:0;
 const {stipend,housing,meals,taxable}=payParts(weeklyAsk,floorWeekly,cap,housingCap);
 if(isTravel&&travelQualified&&verifiedGsa&&weeklyAsk>floorWeekly+cap)
   warnings.push("Pay exceeds the stipend cap: the excess remains taxable.");
 if(state==="AK") warnings.push("Alaska daily overtime rules may apply; payroll review required.");
 if(state==="CA"&&shiftHours>8) warnings.push("California daily overtime/alternative workweek rules require payroll review.");
 const clinical=discipline!=="Non-Clinical";
 const fixed=clinical?settings.clinicalFixedCost:settings.nonClinicalFixedCost;
 const bonus=amount(i.bonus??0),reimbursement=amount(i.reimbursement??0);
 if(!validNonNegative(bonus)||!validNonNegative(reimbursement))return{ready:false,status:"ENTER",problems:["Bonus and reimbursement must be nonnegative."],warnings};
 const weeklyRevenue=billRate*(1-fee)*billableWeightedHours;
 const k=settings.burden+orientation*(1+settings.burden)/(weightedPayHours*weeks);
 const c0=fixed+(bonus*(1+settings.burden)+reimbursement)/weeks;
 const weeklyCost=taxable*(1+k)+stipend+c0;
 const weeklyProfit=weeklyRevenue-weeklyCost;
 const margin=weeklyRevenue>0?weeklyProfit/weeklyRevenue:0;
 const status=weeklyAsk<floorWeekly?"LOW":margin>=settings.greenMargin?"GREEN":margin>=settings.minimumMargin?"YELLOW":"RED";
 function maxOffer(target) {
   const b=weeklyRevenue*(1-target)-c0;
   if(b<floorWeekly*(1+k))return 0;
   if(b-floorWeekly*k<=floorWeekly+cap)return Math.max(0,b-floorWeekly*k);
   return Math.max(0,(b-cap)/(1+k)+cap);
 }
 const maxGreen=maxOffer(settings.greenMargin),maxApproval=maxOffer(settings.minimumMargin),breakEven=maxOffer(0);
 const requiredBill=billableWeightedHours>0&&fee<1
   ?weeklyCost/(1-settings.greenMargin)/billableWeightedHours/(1-fee):0;
 const offerBreakdown=(target)=>{const p=payParts(target,floorWeekly,cap,housingCap);return {...p,taxableHourly:weightedPayHours?p.taxable/weightedPayHours:0};};
 const checks={
  components:Math.abs(taxable+stipend-weeklyAsk)<0.01,
  stipendCap:stipend<=cap+0.001,
  cost:Math.abs(weeklyCost-((taxable*(1+k))+stipend+c0))<0.01,
  requiredBillRate:requiredBill===0||Math.abs((requiredBill*(1-fee)*billableWeightedHours-weeklyCost)/(requiredBill*(1-fee)*billableWeightedHours)-settings.greenMargin)<0.00001,
  greenOffer:maxGreen===0||Math.abs((weeklyRevenue-(offerBreakdown(maxGreen).taxable*(1+k)+offerBreakdown(maxGreen).stipend+c0))/weeklyRevenue-settings.greenMargin)<0.00001,
 };
 return {
  ready:true,status,problems,warnings,program,discipline,clinical,state,zip,
  weeklyAsk,shiftCount,shiftHours,straightHours,otHours,weightedPayHours,billableWeightedHours,
  weeks,billRate,fee,orientation,otBill,taxableFloor,floorWeekly,
  weeklyRevenue,weeklyCost,weeklyProfit,margin,
  taxableWeekly:taxable,taxableHourly:taxable/weightedPayHours,
  housingWeekly:housing,mealsWeekly:meals,stipendWeekly:stipend,candidateWeeklyPay:weeklyAsk,
  overtimePayRate:(taxable/weightedPayHours)*settings.overtimePayMultiplier,
  contractProfit:weeklyProfit*weeks,contractRevenue:weeklyRevenue*weeks,
  totalCost:weeklyCost*weeks,candidateContractPay:weeklyAsk*weeks+bonus+reimbursement,
  weeklyFixedCost:fixed,orientationWeeklyCost:(taxable*orientation*(1+settings.burden))/(weightedPayHours*weeks),
  burdenCost:taxable*settings.burden,bonus,reimbursement,
  maxGreen,maxApproval,breakEven,requiredBill,
  negotiation:[
   {label:"GREEN max (15%)",amount:maxGreen},
   {label:"Ask − $200",amount:Math.max(0,weeklyAsk-200)},
   {label:"Ask − $100",amount:Math.max(0,weeklyAsk-100)},
   {label:"Candidate ask",amount:weeklyAsk},
   {label:"Ask + $100",amount:weeklyAsk+100},
   {label:"Ask + $200",amount:weeklyAsk+200},
   {label:"Approval max (12%)",amount:maxApproval},
  ].map(row=>{const p=offerBreakdown(row.amount);const profit=weeklyRevenue-(p.taxable*(1+k)+p.stipend+c0);return {...row,taxableHourly:p.taxableHourly,margin:weeklyRevenue>0?profit/weeklyRevenue:0};}),
  checks,allChecksPass:Object.values(checks).every(Boolean),gsaVerified:verifiedGsa,
 };
}
export { V9_SETTINGS, V9_DISCIPLINE_FLOORS, V9_STATE_WAGES, calculateV9 };
